import { useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";

import councilPortalService from "api/councilPortalService";
import councilService from "api/councilService";
import { ICouncilSession } from "dto/Council/ICouncil";
import { ENotificationType, notification } from "utils/notification";

import { decodeText } from "./decodeText";
import styles from "./styles.module.scss";

interface Props {
  /** null → nowa sesja; obiekt → edycja metryki. */
  session: ICouncilSession | null;
  onClose: () => void;
  onSaved: (session: ICouncilSession) => void;
}

// Metryka sesji — odwzorowanie SessionMetricModal z Magistratu.
// Pola: Numer sesji, Data sesji, Godzina rozpoczęcia, Tytuł (wyświetlany podczas
// transmisji) oraz przełącznik „Sesja w sali obrad / Sesja online".
// Pole „Teczka" z Magistratu jest tu pominięte — w wariancie Biura rady było ukryte
// (ProductVisibility="AllExceptCouncilOffice"), a w Nota sesja nie wymaga teczki.

const toDatePart = (iso: string | null) => (iso ? iso.slice(0, 10) : "");
const toTimePart = (iso: string | null) => (iso ? iso.slice(11, 16) : "");

const SessionMetricModal = ({ session, onClose, onSaved }: Props) => {
  const isNew = !session;

  const [number, setNumber] = useState(session?.Number ?? "");
  const [title, setTitle] = useState(session?.Title ?? "");
  const [date, setDate] = useState(toDatePart(session?.Date ?? null));
  const [time, setTime] = useState(toTimePart(session?.Date ?? null) || "10:00");
  const [isOnline, setIsOnline] = useState(session?.IsOnline ?? false);
  const [videoUrl, setVideoUrl] = useState(session?.VideoUrl ?? "");
  const [leadId, setLeadId] = useState(session?.LeadByCouncilorId ?? "");

  const councilorsQuery = councilPortalService.useGetCouncilors();
  const councilors = councilorsQuery.data?.data ?? [];
  const [touched, setTouched] = useState(false);

  const { mutate, isLoading } = councilService.useSaveSession();

  const numberError = !number.trim() ? "Numer sesji jest wymagany." : "";
  const dateError = !date ? "Data sesji jest wymagana." : "";
  const canSave = !numberError && !dateError;

  const submit = () => {
    setTouched(true);
    if (!canSave) return;

    // Data i godzina lecą jako lokalny czas bez strefy — BE trzyma DATETIME bez offsetu.
    const dateTime = `${date}T${time || "00:00"}:00`;

    mutate(
      {
        Id: session?.Id ?? null,
        Number: number.trim(),
        Title: title.trim() || null,
        Date: dateTime,
        IsOnline: isOnline,
        VideoUrl: videoUrl.trim() || null,
        LeadByCouncilorId: leadId || null,
      },
      {
        onSuccess: (res) => {
          notification({
            type: ENotificationType.SUCCESS,
            title: isNew ? "Utworzono sesję" : "Zapisano metrykę",
            text: `Sesja ${res.data.Number ?? ""}`,
          });
          onSaved(res.data);
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text:
              (e as { response?: { data?: { Message?: string } } })?.response?.data
                ?.Message ?? "Nie udało się zapisać metryki sesji.",
          }),
      },
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>
          {isNew ? "Nowa sesja" : "Metryka sesji"}
        </div>

        <div className={styles.modalBody}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Numer sesji</label>
            <input
              className={styles.textInput}
              value={number}
              autoFocus
              onChange={(e) => setNumber(e.target.value)}
              placeholder="np. XXIV/2026"
            />
            {touched && numberError && (
              <span className={styles.fieldError}>{numberError}</span>
            )}
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Data sesji</label>
              <input
                className={styles.textInput}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              {touched && dateError && (
                <span className={styles.fieldError}>{dateError}</span>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Godzina rozpoczęcia</label>
              <input
                className={styles.textInput}
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Tytuł (wyświetlany podczas transmisji)
            </label>
            <input
              className={styles.textInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="np. Sesja Rady Miejskiej w …"
            />
          </div>

          <div className={styles.radioRow}>
            <label className={styles.radioOption}>
              <input
                type="radio"
                checked={!isOnline}
                onChange={() => setIsOnline(false)}
              />
              Sesja w sali obrad
            </label>
            <label className={styles.radioOption}>
              <input
                type="radio"
                checked={isOnline}
                onChange={() => setIsOnline(true)}
              />
              Sesja online
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Adres transmisji (opcjonalnie)</label>
            <input
              className={styles.textInput}
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://…"
            />
          </div>

          {/*
            Prowadzący obrady nie jest ozdobnikiem metryki: od jego płci zależy odmiana
            zdań protokołu („Przewodniczący zarządził…" / „Przewodnicząca zarządziła…"),
            a jego nazwisko wchodzi w zdanie o otwarciu sesji.
          */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Obrady prowadzi</label>
            <select
              className={styles.textInput}
              value={leadId}
              onChange={(e) => setLeadId(e.target.value)}
            >
              <option value="">nie wskazano</option>
              {councilors.map((c) => (
                <option key={c.Id} value={c.Id}>
                  {decodeText(c.LastName)} {decodeText(c.FirstName)}
                </option>
              ))}
            </select>
            <span className={styles.fieldLabel}>
              Bez wskazania protokół pisze o przewodniczącym w rodzaju męskim.
            </span>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text={isNew ? "Utwórz" : "Zapisz"}
            onClick={submit}
            loading={isLoading}
            disabled={touched && !canSave}
          />
        </div>
      </div>
    </div>
  );
};

export default SessionMetricModal;
